import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ASSETS, PRICE, WHOP_URL } from "../data/content";

function useCountdown(hours = 23) {
    const [remaining, setRemaining] = useState(hours * 3600);
    useEffect(() => {
        const t = setInterval(
            () => setRemaining((r) => (r > 0 ? r - 1 : 0)),
            1000,
        );
        return () => clearInterval(t);
    }, []);
    const h = String(Math.floor(remaining / 3600)).padStart(2, "0");
    const m = String(Math.floor((remaining % 3600) / 60)).padStart(2, "0");
    const s = String(remaining % 60).padStart(2, "0");
    return { h, m, s };
}

export default function Pricing() {
    const { h, m, s } = useCountdown(23);

    return (
        <section
            id="pricing"
            data-testid="pricing-section"
            className="relative py-24 sm:py-36 border-t border-white/5"
        >
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at center, rgba(122,0,0,0.18), transparent 70%)",
                }}
            />
            <div className="relative max-w-5xl mx-auto px-6 sm:px-10">
                <div className="text-center mb-14">
                    <div className="overline mb-5">The Acquisition</div>
                    <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-[#f4f4f0]">
                        One book.{" "}
                        <span className="italic text-[#990000]">
                            One filter.
                        </span>
                    </h2>
                    <p className="mt-5 max-w-xl mx-auto text-neutral-400">
                        The price below exists for a limited window. Once the
                        seats fill, the door closes at the listed retail.
                    </p>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                    className="relative bg-[#070303] border border-[#7A0000]/30 p-8 sm:p-14"
                    style={{
                        boxShadow:
                            "0 0 80px -20px rgba(122,0,0,0.55), inset 0 0 0 1px rgba(122,0,0,0.06)",
                    }}
                    data-testid="pricing-card"
                >
                    {/* Discount badge */}
                    <div
                        className="absolute -top-3 left-8 bg-[#7A0000] text-[#f4f4f0] px-4 py-2 text-[10px] tracking-[0.32em] uppercase"
                        data-testid="pricing-discount-badge"
                    >
                        {PRICE.discountLabel} · Launch Window
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <div className="font-serif-display italic text-[#990000] text-sm tracking-wider mb-3">
                                The Varek Code — Digital Edition
                            </div>
                            <div className="flex items-baseline gap-4 mb-2">
                                <span
                                    className="font-serif-display text-6xl sm:text-7xl text-[#f4f4f0]"
                                    data-testid="pricing-price"
                                >
                                    {PRICE.discounted}
                                </span>
                                <span className="line-through text-neutral-500 text-xl">
                                    {PRICE.original}
                                </span>
                            </div>
                            <div className="text-xs tracking-[0.24em] uppercase text-neutral-500 mb-8">
                                One-time payment · Instant delivery
                            </div>

                            <ul className="space-y-3 text-neutral-300 text-base">
                                {[
                                    "All 5 phases — the full system, unredacted.",
                                    "The 30 Days Transformation Protocol.",
                                    "The Daily Alpha Operating System.",
                                    "The 24 Non-Negotiable Rules of Power.",
                                    "Lifetime access — including future revisions.",
                                    "Instant delivery after checkout.",
                                ].map((f, i) => (
                                    <li
                                        key={i}
                                        className="flex items-start gap-3"
                                        data-testid={`pricing-feature-${i}`}
                                    >
                                        <span className="text-[#7A0000] mt-1">
                                            ✕
                                        </span>
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>

                            <a
                                href={WHOP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="pricing-cta-button"
                                className="btn-primary mt-10 w-full sm:w-auto justify-between"
                            >
                                <span>
                                    Acquire The Code · {PRICE.discounted}
                                </span>
                                <span aria-hidden>→</span>
                            </a>

                            <div className="mt-5 flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-neutral-500">
                                <span className="inline-block w-1 h-1 bg-[#7A0000] ember" />
                                Secure checkout · No subscription
                            </div>
                        </div>

                        <div className="lg:col-span-5 flex flex-col items-center text-center gap-6">
                            <img
                                src={ASSETS.book}
                                alt="The Varek Code book"
                                className="w-52 shadow-[0_30px_80px_-15px_rgba(0,0,0,0.9)]"
                                style={{
                                    transform:
                                        "perspective(1200px) rotateY(-12deg)",
                                }}
                                data-testid="pricing-book-image"
                            />
                            <div className="border-t border-white/5 w-full pt-6">
                                <div className="overline mb-3">
                                    Window Closes In
                                </div>
                                <div
                                    className="font-mono-accent text-3xl text-[#f4f4f0] flex justify-center gap-2"
                                    data-testid="pricing-countdown"
                                >
                                    <span>{h}</span>
                                    <span className="text-[#7A0000]">:</span>
                                    <span>{m}</span>
                                    <span className="text-[#7A0000]">:</span>
                                    <span>{s}</span>
                                </div>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mt-2">
                                    Hours · Minutes · Seconds
                                </div>
                            </div>
                            <img
                                src={ASSETS.seal}
                                alt="Authority seal"
                                className="w-20 opacity-80"
                            />
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
